<script setup lang="ts">
import { ref, computed } from 'vue';
import { useCustomerMgmt } from '@ilteducation/customer-mgmt';
import type { Context } from '@ilteducation/customer-mgmt';
import SearchInput from '@/components/my-students/SearchInput.vue';
import { useLocale } from '@/plugins/translation';

const compProps = withDefaults(
  defineProps<{
    context: Context;
    extendedOutput?: boolean;
    preSelectedIds?: string[];
  }>(),
  {
    extendedOutput: () => false,
    preSelectedIds: () => [],
  },
);
const emit = defineEmits<{ (e: 'change-select-value', selectValue: any | any[]): void }>();

const { selectedItems, displayedItems, isFetching, setSelectedItems, fetchSearch, infiniteScrollIntersect } =
  useCustomerMgmt({
    context: compProps.context,
    preSelectedIds: compProps.preSelectedIds,
  });
const { t, l } = useLocale();

const errorMessages = ref([]);
const selectValue = computed({
  get: () => selectedItems.value,
  set: (value) => {
    if (value !== undefined) {
      emit('change-select-value', value);
      setSelectedItems(value);
    }
  },
});
</script>

<template>
  <v-select
    v-bind="{ ...compProps, ...$attrs }"
    v-model="selectValue"
    :items="displayedItems"
    :error-messages="errorMessages"
    autocomplete="organization"
    return-object
    single-line
    item-title="uid"
    variant="outlined"
    rounded="md"
    density="compact"
    hide-details
    class="custom-select"
  >
    <template #no-data>
      <div v-if="!isFetching" tabindex="-1" class="v-list-item theme--light">
        <div class="v-list-item__content">
          <div class="v-list-item__title">{{ t(l.my_page.no_data) }}</div>
        </div>
      </div>
      <div v-else></div>
    </template>
    <template #selection="{ item }">
      <v-list-item :title="item.raw.name"></v-list-item>
    </template>
    <template #item="{ props, item }">
      <v-list-item v-bind="props" :title="item.raw.name"> </v-list-item>
    </template>
    <template #prepend-item>
      <div class="prepend-sticky">
        <v-container class="search-container">
          <SearchInput :context="compProps.context" @do-search="fetchSearch" />
        </v-container>
      </div>
    </template>
    <template #append-item>
      <div v-intersect="infiniteScrollIntersect" class="pt-10 pb-10 text-center">
        <v-progress-circular
          v-if="isFetching"
          :size="50"
          color="primary"
          indeterminate
          :aria-label="t(l.label_loading)"
        />
      </div>
    </template>
  </v-select>
</template>

<style lang="scss">
.v-menu > .v-overlay__content > .v-list {
  padding: 0;
}
</style>
<style lang="scss" scoped>
:deep(.v-field__outline__end) {
  border-color: rgb(var(--v-theme-secondary));
  border-width: 1px;
  border-left-width: 0;
  opacity: 1;
}
:deep(.v-field__outline__start) {
  border-color: rgb(var(--v-theme-secondary));
  border-width: 1px;
  border-right-width: 0;
  opacity: 1;
}
:deep(.v-field__input) {
  padding: 0;
}

.prepend-sticky {
  position: sticky;
  top: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  background: white;
  z-index: 10;
  border-bottom: 1px rgb(var(--v-theme-gray), 0.2) solid;
}
</style>
